/**
 * Normalizes checklist schedule event payloads from the API so "Checklist - My"
 * filtering works whether the backend uses camelCase or snake_case, or embeds
 * assignee as an IRI string / @id without numeric id (common across categorized buckets).
 */

import {
	isUserMemberOfAssignmentGroup,
	isUserInAssignmentGroupById,
	parseGroupIdFromRef,
} from './assignmentGroupMembership';

const parseNumericIdFromTail = (raw: string): number | null => {
	const match = raw.match(/(\d+)$/);
	if (match) return Number(match[1]);
	const n = Number(raw);
	return Number.isFinite(n) ? n : null;
};

export function parseUserIdFromRef(value: unknown): number | null {
	if (value == null) return null;
	if (typeof value === 'number' && Number.isFinite(value)) return value;
	if (typeof value === 'string') return parseNumericIdFromTail(value);
	if (typeof value === 'object') {
		const o = value as Record<string, unknown>;
		if (o.id != null) return parseUserIdFromRef(o.id);
		if (typeof o['@id'] === 'string') return parseNumericIdFromTail(o['@id']);
	}
	return null;
}

export function getChecklistSchedule(event: unknown): Record<string, unknown> | null {
	if (!event || typeof event !== 'object') return null;
	const e = event as Record<string, unknown>;
	const schedule = e.checklistSchedule ?? e.checklist_schedule;
	if (schedule && typeof schedule === 'object') return schedule as Record<string, unknown>;
	return null;
}

export function getAssignmentType(schedule: Record<string, unknown> | null): string | undefined {
	if (!schedule) return undefined;
	const t = schedule.assignmentType ?? schedule.assignment_type;
	return typeof t === 'string' ? t : undefined;
}

export function getAssigneeUserId(schedule: Record<string, unknown> | null): number | null {
	if (!schedule) return null;
	const assignee = schedule.assignee ?? schedule.assignee_user;
	if (assignee == null) return null;
	return parseUserIdFromRef(assignee);
}

function parseCompletedById(completedBy: unknown): number | null {
	if (completedBy == null) return null;
	if (typeof completedBy === 'object') {
		const o = completedBy as Record<string, unknown>;
		if (typeof o['@id'] === 'string') return parseNumericIdFromTail(o['@id']);
		if (o.id != null) return parseUserIdFromRef(o.id);
	}
	if (typeof completedBy === 'string') return parseNumericIdFromTail(completedBy);
	return null;
}

export function getAssignedGroupFromSchedule(
	schedule: Record<string, unknown> | null
): unknown {
	if (!schedule) return null;
	return schedule.group ?? schedule.assignedGroup ?? schedule.assigned_group ?? null;
}

export function getAssignedGroupIdFromSchedule(
	schedule: Record<string, unknown> | null
): number | null {
	return parseGroupIdFromRef(getAssignedGroupFromSchedule(schedule));
}

/**
 * Returns true if this event should be visible under "Checklist - My":
 * assigned to the current user, assigned to a group they belong to, or completedBy matches.
 */
export function isChecklistEventAssignedToCurrentUser(
	event: unknown,
	currentUserId: number | null,
	userGroupIds?: ReadonlySet<number>,
	currentSecurityRoleId?: number | null
): boolean {
	if (currentUserId == null) return false;
	const schedule = getChecklistSchedule(event);
	if (!schedule) return false;

	const assignmentType = getAssignmentType(schedule);
	if (assignmentType === 'group') {
		const assignedGroup = getAssignedGroupFromSchedule(schedule);
		const groupId = parseGroupIdFromRef(assignedGroup);
		if (isUserInAssignmentGroupById(groupId, userGroupIds)) {
			return true;
		}
		if (assignedGroup && typeof assignedGroup === 'object') {
			return isUserMemberOfAssignmentGroup(
				assignedGroup,
				currentUserId,
				currentSecurityRoleId ?? null
			);
		}
		return false;
	}

	const assigneeId = getAssigneeUserId(schedule);
	if (assigneeId != null && Number(assigneeId) === Number(currentUserId)) {
		return true;
	}

	if (!event || typeof event !== 'object') return false;
	const e = event as Record<string, unknown>;
	const completedBy = e.completedBy ?? e.completed_by;
	const completedById = parseCompletedById(completedBy);
	if (completedById != null && Number(completedById) === Number(currentUserId)) {
		return true;
	}

	return false;
}
